import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FaPlus, FaEdit, FaTrash, FaCalendarAlt, FaClock, FaUpload } from 'react-icons/fa';
import { blogAPI, uploadAPI } from '../utils/api';

const Container = styled.div`
  min-height: 100vh;
  background: var(--secondary-50);
  padding: var(--space-xl);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-2xl);
  
  h1 {
    color: var(--secondary-900);
    font-size: 2rem;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  
  ${props => props.$primary ? `
    background: var(--primary-600);
    color: white;
    
    &:hover {
      background: var(--primary-700);
    }
  ` : props.$danger ? `
    background: var(--error-600);
    color: white;
    
    &:hover {
      background: var(--error-700);
    }
  ` : `
    background: white;
    color: var(--secondary-700);
    border: 1px solid var(--secondary-300);
    
    &:hover {
      background: var(--secondary-50);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PostsList = styled.div`
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
`;

const PostItem = styled.div`
  padding: var(--space-lg);
  border-bottom: 1px solid var(--secondary-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: var(--secondary-50);
  }
`;

const PostInfo = styled.div`
  flex: 1;
  
  h3 {
    margin-bottom: var(--space-xs);
    color: var(--secondary-900);
  }
  
  p {
    color: var(--secondary-600);
    font-size: 0.875rem;
    margin-bottom: var(--space-xs);
  }
`;

const PostMeta = styled.div`
  display: flex;
  gap: var(--space-md);
  font-size: 0.875rem;
  color: var(--secondary-500);
  
  span {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: var(--space-sm);
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-xl);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-2xl);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: var(--space-lg);
  
  label {
    display: block;
    margin-bottom: var(--space-sm);
    font-weight: 500;
    color: var(--secondary-700);
  }
  
  input, select {
    width: 100%;
    padding: var(--space-md);
    border: 1px solid var(--secondary-300);
    border-radius: var(--radius-md);
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
  }
`;

const QuillWrapper = styled.div`
  .ql-container {
    min-height: 300px;
    font-size: 1rem;
    border-radius: var(--radius-md);
  }
  
  .ql-editor {
    min-height: 300px;
    
    /* Ensure proper visualization of content */
    p, h1, h2, h3, h4, h5, h6 {
      margin: 0.5em 0;
    }
    
    ul, ol {
      padding-left: 1.5em;
      margin: 0.5em 0;
    }
    
    li {
      margin: 0.25em 0;
    }
    
    blockquote {
      border-left: 4px solid var(--primary-600);
      margin: 1em 0;
      padding: 0.5em 1em;
      background: var(--secondary-50);
    }
    
    code {
      background: var(--secondary-100);
      padding: 0.2em 0.4em;
      border-radius: var(--radius-sm);
      font-family: 'Courier New', monospace;
    }
    
    pre {
      background: var(--secondary-100);
      padding: 1em;
      border-radius: var(--radius-md);
      overflow-x: auto;
      margin: 1em 0;
    }
    
    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 1em 0;
    }
  }
  
  .ql-toolbar {
    border-top-left-radius: var(--radius-md);
    border-top-right-radius: var(--radius-md);
  }
  
  .ql-container {
    border-bottom-left-radius: var(--radius-md);
    border-bottom-right-radius: var(--radius-md);
  }
`;

const ImageUpload = styled.div`
  margin-bottom: var(--space-lg);
  
  input[type="file"] {
    display: none;
  }
  
  label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-md) var(--space-lg);
    background: var(--secondary-100);
    border: 1px solid var(--secondary-300);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-normal);
    
    &:hover {
      background: var(--secondary-200);
    }
  }
`;

const ImagePreview = styled.div`
  margin-top: var(--space-md);
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: var(--radius-md);
    border: 1px solid var(--secondary-300);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: var(--space-lg);
  margin-top: var(--space-sm);
  
  label {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    cursor: pointer;
    
    input[type="checkbox"] {
      width: auto;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-2xl);
  
  h2 {
    color: var(--secondary-900);
    font-size: 1.5rem;
  }
`;

const StatusBadge = styled.span`
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  
  ${props => props.$published ? `
    background: var(--success-100);
    color: var(--success-700);
  ` : `
    background: var(--secondary-100);
    color: var(--secondary-700);
  `}
`;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: { en: '', de: '' },
    excerpt: { en: '', de: '' },
    content: { en: '', de: '' },
    category: 'Agile',
    tags: '',
    isPublished: false,
    isFeatured: false,
    scheduledPublishDate: '',
    scheduledPublishTime: '',
    featuredImage: ''
  });
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const quillRefEn = useRef(null);
  const quillRefDe = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.getAllAdmin();
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle multilingual fields (title.en, title.de, etc.)
    if (name.includes('.')) {
      const [field, lang] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [lang]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleContentChange = (value, lang = 'en') => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [lang]: value
      }
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('createThumbnail', 'true');
      formData.append('format', 'webp');
      formData.append('quality', '80');

      const response = await uploadAPI.uploadImage(formData);
      setFormData(prev => ({
        ...prev,
        featuredImage: response.data.image.url
      }));
      setImagePreview(response.data.image.url);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const postData = {
        ...formData,
        tags: tagsArray,
        scheduledPublishDate: formData.scheduledPublishDate && formData.scheduledPublishTime
          ? new Date(`${formData.scheduledPublishDate}T${formData.scheduledPublishTime}`).toISOString()
          : null
      };

      if (editingPost) {
        await blogAPI.update(editingPost._id, postData);
      } else {
        await blogAPI.create(postData);
      }

      setShowModal(false);
      setEditingPost(null);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post. Please try again.');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    const scheduledDate = post.scheduledPublishDate ? new Date(post.scheduledPublishDate) : null;
    setFormData({
      title: post.title || { en: '', de: '' },
      excerpt: post.excerpt || { en: '', de: '' },
      content: post.content || { en: '', de: '' },
      category: post.category || 'Agile',
      tags: post.tags?.join(', ') || '',
      isPublished: post.isPublished || false,
      isFeatured: post.isFeatured || false,
      scheduledPublishDate: scheduledDate ? scheduledDate.toISOString().split('T')[0] : '',
      scheduledPublishTime: scheduledDate ? scheduledDate.toTimeString().slice(0, 5) : '',
      featuredImage: post.featuredImage || ''
    });
    setImagePreview(post.featuredImage || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogAPI.delete(id);
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: { en: '', de: '' },
      excerpt: { en: '', de: '' },
      content: { en: '', de: '' },
      category: 'Agile',
      tags: '',
      isPublished: false,
      isFeatured: false,
      scheduledPublishDate: '',
      scheduledPublishTime: '',
      featuredImage: ''
    });
    setImagePreview('');
  };

  const handleNewPost = () => {
    setEditingPost(null);
    resetForm();
    setShowModal(true);
  };

  const handleImageUploadQuillEn = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        setUploadingImage(true);
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);
        uploadFormData.append('createThumbnail', 'true');
        uploadFormData.append('format', 'webp');
        uploadFormData.append('quality', '80');

        const response = await uploadAPI.uploadImage(uploadFormData);
        const imageUrl = response.data.image.url;
        
        // Get the Quill editor instance and insert the image
        if (quillRefEn.current) {
          const quill = quillRefEn.current.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);
        }
      } catch (error) {
        console.error('Image upload error:', error);
        alert('Error uploading image. Please try again.');
      } finally {
        setUploadingImage(false);
      }
    };
  }, []);

  const handleImageUploadQuillDe = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        setUploadingImage(true);
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);
        uploadFormData.append('createThumbnail', 'true');
        uploadFormData.append('format', 'webp');
        uploadFormData.append('quality', '80');

        const response = await uploadAPI.uploadImage(uploadFormData);
        const imageUrl = response.data.image.url;
        
        // Get the Quill editor instance and insert the image
        if (quillRefDe.current) {
          const quill = quillRefDe.current.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);
        }
      } catch (error) {
        console.error('Image upload error:', error);
        alert('Error uploading image. Please try again.');
      } finally {
        setUploadingImage(false);
      }
    };
  }, []);

  const quillModulesEn = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: handleImageUploadQuillEn
      }
    },
    clipboard: {
      matchVisual: true,
      matchVisualOnly: false,
      // Preserve formatting from external editors
      keepSelection: true
    }
  }), [handleImageUploadQuillEn]);

  const quillModulesDe = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: handleImageUploadQuillDe
      }
    },
    clipboard: {
      matchVisual: true,
      matchVisualOnly: false,
      // Preserve formatting from external editors
      keepSelection: true
    }
  }), [handleImageUploadQuillDe]);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link', 'image'
  ];

  if (loading) {
    return (
      <Container>
        <div>Loading...</div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h1>Blog Management</h1>
        <Button $primary onClick={handleNewPost}>
          <FaPlus />
          New Post
        </Button>
      </Header>

      <PostsList>
        {posts.length === 0 ? (
          <PostItem>
            <p>No blog posts yet. Create your first post!</p>
          </PostItem>
        ) : (
          posts.map(post => (
            <PostItem key={post._id}>
              <PostInfo>
                <h3>{post.title?.en || post.title || 'No title'}</h3>
                <p>{post.excerpt?.en || post.excerpt || 'No excerpt'}</p>
                <PostMeta>
                  <StatusBadge $published={post.isPublished}>
                    {post.isPublished ? 'Published' : 'Draft'}
                  </StatusBadge>
                  <span>
                    <FaCalendarAlt />
                    {new Date(post.createdAt).toLocaleDateString()}
                  </span>
                  {post.scheduledPublishDate && (
                    <span>
                      <FaClock />
                      Scheduled: {new Date(post.scheduledPublishDate).toLocaleString()}
                    </span>
                  )}
                  <span>Category: {post.category}</span>
                </PostMeta>
              </PostInfo>
              <Actions>
                <Button onClick={() => handleEdit(post)}>
                  <FaEdit />
                  Edit
                </Button>
                <Button $danger onClick={() => handleDelete(post._id)}>
                  <FaTrash />
                  Delete
                </Button>
              </Actions>
            </PostItem>
          ))
        )}
      </PostsList>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
              <Button onClick={() => setShowModal(false)}>Close</Button>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <label>Title (English) *</label>
                <input
                  type="text"
                  name="title.en"
                  value={formData.title.en || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label>Title (German) *</label>
                <input
                  type="text"
                  name="title.de"
                  value={formData.title.de || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label>Excerpt (English) *</label>
                <input
                  type="text"
                  name="excerpt.en"
                  value={formData.excerpt.en || ''}
                  onChange={handleInputChange}
                  maxLength={300}
                  required
                />
                <small>{(formData.excerpt.en || '').length}/300 characters</small>
              </FormGroup>

              <FormGroup>
                <label>Excerpt (German) *</label>
                <input
                  type="text"
                  name="excerpt.de"
                  value={formData.excerpt.de || ''}
                  onChange={handleInputChange}
                  maxLength={300}
                  required
                />
                <small>{(formData.excerpt.de || '').length}/300 characters</small>
              </FormGroup>

              <FormGroup>
                <label>Featured Image</label>
                <ImageUpload>
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  <label htmlFor="image-upload">
                    <FaUpload />
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </label>
                  {(imagePreview || formData.featuredImage) && (
                    <ImagePreview>
                      <img 
                        src={imagePreview || formData.featuredImage} 
                        alt="Featured" 
                      />
                    </ImagePreview>
                  )}
                </ImageUpload>
              </FormGroup>

              <FormGroup>
                <label>Content (English) *</label>
                <QuillWrapper>
                  <ReactQuill
                    ref={quillRefEn}
                    theme="snow"
                    value={formData.content.en || ''}
                    onChange={(value) => handleContentChange(value, 'en')}
                    modules={quillModulesEn}
                    formats={quillFormats}
                    placeholder="Write your blog post content here..."
                  />
                </QuillWrapper>
              </FormGroup>

              <FormGroup>
                <label>Content (German) *</label>
                <QuillWrapper>
                  <ReactQuill
                    ref={quillRefDe}
                    theme="snow"
                    value={formData.content.de || ''}
                    onChange={(value) => handleContentChange(value, 'de')}
                    modules={quillModulesDe}
                    formats={quillFormats}
                    placeholder="Schreiben Sie hier den Inhalt Ihres Blog-Beitrags..."
                  />
                </QuillWrapper>
              </FormGroup>

              <FormGroup>
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Agile">Agile</option>
                  <option value="Scrum">Scrum</option>
                  <option value="Leadership">Leadership</option>
                  <option value="Coaching">Coaching</option>
                  <option value="Team Building">Team Building</option>
                  <option value="Digital Transformation">Digital Transformation</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label>Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="agile, scrum, coaching"
                />
              </FormGroup>

              <FormGroup>
                <label>Schedule Publishing</label>
                <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
                  <input
                    type="date"
                    name="scheduledPublishDate"
                    value={formData.scheduledPublishDate}
                    onChange={handleInputChange}
                  />
                  <input
                    type="time"
                    name="scheduledPublishTime"
                    value={formData.scheduledPublishTime}
                    onChange={handleInputChange}
                  />
                </div>
              </FormGroup>

              <CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleInputChange}
                  />
                  Published
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                  />
                  Featured
                </label>
              </CheckboxGroup>

              <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-xl)' }}>
                <Button $primary type="submit">
                  {editingPost ? 'Update Post' : 'Create Post'}
                </Button>
                <Button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminBlog;
